import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { supabase } from '../lib/supabase';
import { usePricing } from '../hooks/usePricing';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from '../components/ui/select';
import { AlertCircle, Megaphone } from 'lucide-react';

const URGENCY_OPTIONS = ['anytime', 'this_week', 'asap'];
const TITLE_MIN = 3;
const TITLE_MAX = 120;
const DESC_MIN = 10;
const DESC_MAX = 2000;

/**
 * Client "I need X" form (route /services/requests/new). Posts to POST /api/service-requests
 * and navigates to the new request's detail page on success. Flag-gated: renders the same
 * coming-soon shell as Services.js when pricing.services_enabled is false.
 */
const PostServiceRequest = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pricing, loading: pricingLoading } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [categoriesGrouped, setCategoriesGrouped] = useState({});
  const [categoriesError, setCategoriesError] = useState(false);
  const [categoriesReloadKey, setCategoriesReloadKey] = useState(0);
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [urgency, setUrgency] = useState('anytime');

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load the grouped category taxonomy for the select (mirrors ProListingForm).
  useEffect(() => {
    if (!servicesEnabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/service-categories');
        if (cancelled) return;
        setCategoriesGrouped(res.data?.grouped || {});
        setCategoriesError(false);
      } catch (err) {
        if (!cancelled) { setCategoriesGrouped({}); setCategoriesError(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [servicesEnabled, categoriesReloadKey]);

  const groupNames = useMemo(() => Object.keys(categoriesGrouped), [categoriesGrouped]);

  const validate = () => {
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();
    if (!categoryId) return t('services.reqErrCategoryRequired');
    if (!trimmedTitle) return t('services.reqErrTitleRequired');
    if (trimmedTitle.length < TITLE_MIN || trimmedTitle.length > TITLE_MAX) {
      return t('services.reqErrTitleLength');
    }
    if (!trimmedDesc) return t('services.reqErrDescriptionRequired');
    if (trimmedDesc.length < DESC_MIN || trimmedDesc.length > DESC_MAX) {
      return t('services.reqErrDescriptionLength');
    }
    if (!municipality) return t('services.reqErrMunicipalityRequired');
    if (budgetMin !== '' && budgetMax !== '' && Number(budgetMin) > Number(budgetMax)) {
      return t('services.reqErrBudgetOrder');
    }
    return '';
  };

  const mapApiError = (err) => {
    const status = err?.response?.status;
    const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
    if (status === 429) return detail || t('services.reqRateLimited');
    if (status === 400) return detail || t('services.reqErrGeneric');
    return detail || t('services.reqErrGeneric');
  };

  const submit = async () => {
    if (submitting) return;
    setErrorMessage('');

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      const payload = {
        category_id: categoryId,
        title: title.trim(),
        description: description.trim(),
        municipality,
        urgency,
      };
      if (budgetMin !== '') payload.budget_min = Number(budgetMin);
      if (budgetMax !== '') payload.budget_max = Number(budgetMax);

      const res = await api.post('/api/service-requests', payload, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const request = res.data?.request;
      toast.success(t('services.reqPostSuccess'));
      if (request?.id) {
        navigate(`/services/requests/${request.id}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const message = mapApiError(err);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!pricingLoading && !servicesEnabled) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('services.comingSoonTitle')}</h1>
          <p className="text-muted-foreground">{t('services.comingSoonBody')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">{t('services.reqPostTitle')}</h1>
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-muted-foreground">
            <Megaphone size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <span>{t('services.reqPostSubtitle')}</span>
          </div>
        </div>

        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {/* Category (grouped) */}
          <div className="space-y-2">
            <Label htmlFor="req-category">{t('services.reqFormCategory')}</Label>
            <Select value={categoryId || undefined} onValueChange={setCategoryId}>
              <SelectTrigger id="req-category" aria-label={t('services.reqFormCategory')}>
                <SelectValue placeholder={t('services.reqFormCategoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {groupNames.map((group) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {(categoriesGrouped[group] || []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {categoriesError && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                <span className="flex items-center gap-2">
                  <AlertCircle size={16} className="shrink-0" aria-hidden="true" />
                  {t('services.errorGeneric')}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setCategoriesReloadKey((k) => k + 1)}
                >
                  {t('services.retry')}
                </Button>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="req-title">{t('services.reqFormTitle')}</Label>
            <Input
              id="req-title"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('services.reqFormTitlePlaceholder')}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="req-description">{t('services.reqFormDescription')}</Label>
            <Textarea
              id="req-description"
              value={description}
              maxLength={DESC_MAX}
              rows={5}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('services.reqFormDescriptionPlaceholder')}
            />
          </div>

          {/* Municipality (single select) */}
          <div className="space-y-2">
            <Label htmlFor="req-municipality">{t('services.reqFormMunicipality')}</Label>
            <Select value={municipality || undefined} onValueChange={setMunicipality}>
              <SelectTrigger id="req-municipality" aria-label={t('services.reqFormMunicipality')}>
                <SelectValue placeholder={t('services.reqFormMunicipalityPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {BOHOL_MUNICIPALITIES.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Budget (optional) */}
          <div className="space-y-2">
            <Label>
              {t('services.reqFormBudget')}{' '}
              <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
            </Label>
            <p className="text-xs text-muted-foreground">{t('services.reqFormBudgetHint')}</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="req-budget-min" className="text-xs">{t('services.reqFormBudgetMin')}</Label>
                <Input
                  id="req-budget-min"
                  type="number"
                  min="0"
                  value={budgetMin}
                  onChange={(e) => setBudgetMin(e.target.value)}
                  placeholder="₱"
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label htmlFor="req-budget-max" className="text-xs">{t('services.reqFormBudgetMax')}</Label>
                <Input
                  id="req-budget-max"
                  type="number"
                  min="0"
                  value={budgetMax}
                  onChange={(e) => setBudgetMax(e.target.value)}
                  placeholder="₱"
                />
              </div>
            </div>
          </div>

          {/* Urgency (radio) */}
          <div className="space-y-2">
            <Label>{t('services.reqFormUrgency')}</Label>
            <RadioGroup value={urgency} onValueChange={setUrgency} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {URGENCY_OPTIONS.map((u) => (
                <label
                  key={u}
                  htmlFor={`urgency-${u}`}
                  className={`flex items-center gap-2 rounded-lg border p-3 text-sm cursor-pointer ${urgency === u ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <RadioGroupItem value={u} id={`urgency-${u}`} />
                  {t(`services.reqUrgency_${u}`)}
                </label>
              ))}
            </RadioGroup>
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="pt-2">
            <Button type="submit" disabled={submitting} className="w-full min-h-[44px]">
              {submitting ? t('services.reqSubmitting') : t('services.reqSubmit')}
            </Button>
          </div>
        </form>
      </div>
      <BottomNav profile={profile} />
    </div>
  );
};

export default PostServiceRequest;
